/**
 * 첨부파일
 * ---------------------------------------------------------------------------
 * 사업장이나 일지에 증빙을 붙인다. 계약서, 측정 사진, 위탁·수탁 확인서 같은
 * 것들이다.
 *
 * 드래그로 여러 장
 *   현장에서는 사진을 대여섯 장씩 한꺼번에 올린다. 한 장씩 고르게 하면
 *   그만큼 안 올리게 된다.
 *
 * 실패한 것만 다시
 *   한 장이 형식 문제로 걸려도 나머지는 저장된다. 실패한 파일의 이름과
 *   이유를 남겨 두어 그것만 다시 고를 수 있게 한다 — 여섯 장을 통째로
 *   다시 고르게 하면 사용자는 그냥 포기한다.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Empty,
  Input,
  List,
  Popconfirm,
  Space,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, EyeOutlined, InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import { api, errorMessage } from '../lib/api';
import { canPreview, FilePreview, type PreviewTarget } from './FilePreview';

const { Text } = Typography;

export interface Attachment {
  attachmentId: string;
  siteId: string | null;
  dailyLogId: string | null;
  fileName: string;
  fileSize: number;
  contentType: string;
  category: string | null;
  note: string | null;
  createdAt: string;
}

interface UploadResult {
  saved: Attachment[];
  failed: Array<{ fileName: string; reason: string }>;
}

/** 붙는 대상. 둘 중 하나만 준다. */
export interface AttachmentOwner {
  siteId?: string;
  dailyLogId?: string;
}

export function AttachmentPanel({
  owner,
  disabled,
  title = '첨부파일',
}: {
  owner: AttachmentOwner;
  /** 확인완료된 일지처럼 더 고칠 수 없는 상태. */
  disabled?: boolean;
  title?: string;
}) {
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<UploadFile[]>([]);
  const [category, setCategory] = useState('');
  const [failed, setFailed] = useState<UploadResult['failed']>([]);
  /** 미리보기로 열어 둔 파일. null 이면 닫힌 상태다. */
  const [preview, setPreview] = useState<PreviewTarget | null>(null);

  const params = owner.dailyLogId ? { dailyLogId: owner.dailyLogId } : { siteId: owner.siteId };
  const key = ['attachments', owner.dailyLogId ?? owner.siteId];
  const enabled = Boolean(owner.dailyLogId ?? owner.siteId);

  const list = useQuery<Attachment[]>({
    queryKey: key,
    enabled,
    queryFn: async () => (await api.get<Attachment[]>('/attachments', { params })).data,
  });

  const upload = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      for (const [k, v] of Object.entries(params)) if (v) form.append(k, v);
      if (category.trim()) form.append('category', category.trim());
      for (const file of picked) {
        if (file.originFileObj) form.append('files', file.originFileObj);
      }
      return (await api.post<UploadResult>('/attachments', form)).data;
    },
    onSuccess: async (result) => {
      setPicked([]);
      setFailed(result.failed);

      if (result.saved.length > 0) message.success(`${result.saved.length}개를 올렸습니다.`);
      if (result.failed.length > 0) message.warning(`${result.failed.length}개는 올리지 못했습니다.`);

      await queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: async (attachmentId: string) => api.delete(`/attachments/${attachmentId}`),
    onSuccess: async () => {
      message.success('첨부를 지웠습니다.');
      await queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err) => message.error(errorMessage(err)),
  });

  /**
   * 내려받기.
   *
   * 토큰이 필요해 링크를 그냥 열 수 없다. 본문을 받아 브라우저가 저장하게 한다.
   */
  const download = async (item: Attachment) => {
    try {
      const response = await api.get(`/attachments/${item.attachmentId}/file`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data as Blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = item.fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  if (!enabled) return null;

  return (
    <Card size="small" title={title} loading={list.isLoading}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {!disabled && (
          <>
            <Upload.Dragger
              multiple
              fileList={picked}
              beforeUpload={() => false}
              onChange={({ fileList }) => setPicked(fileList)}
              onRemove={(file) => setPicked((prev) => prev.filter((f) => f.uid !== file.uid))}
              style={{ padding: '8px 0' }}
            >
              <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">여기로 끌어다 놓거나 눌러서 고르세요</p>
              <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                여러 개를 한 번에 올릴 수 있습니다. 실행 파일(.exe, .bat 등)은 올릴 수 없습니다.
              </p>
            </Upload.Dragger>

            <Space wrap>
              <Input
                placeholder="분류 (예: 측정사진, 계약서)"
                style={{ width: 220 }}
                maxLength={40}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
              <Button
                type="primary"
                disabled={picked.length === 0}
                loading={upload.isPending}
                onClick={() => upload.mutate()}
              >
                {picked.length > 0 ? `${picked.length}개 올리기` : '올리기'}
              </Button>
            </Space>
          </>
        )}

        {failed.length > 0 && (
          <Alert
            type="warning"
            showIcon
            closable
            onClose={() => setFailed([])}
            message="올리지 못한 파일"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {failed.map((f) => (
                  <li key={f.fileName}>
                    <Text code>{f.fileName}</Text> — {f.reason}
                  </li>
                ))}
              </ul>
            }
          />
        )}

        <List<Attachment>
          size="small"
          dataSource={list.data ?? []}
          locale={{
            emptyText: <Empty description="첨부한 파일이 없습니다" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
          }}
          renderItem={(item) => (
            <List.Item
              actions={[
                /*
                  볼 수 있는 형식(PDF·이미지)에만 미리보기를 둔다. 한글·엑셀에
                  단추를 띄워 봐야 빈 네모만 보여 주게 된다.
                */
                ...(canPreview(item.fileName, item.contentType)
                  ? [
                      <Button
                        key="preview"
                        size="small"
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() =>
                          setPreview({
                            fileName: item.fileName,
                            contentType: item.contentType,
                            load: async () =>
                              (
                                await api.get(`/attachments/${item.attachmentId}/file`, {
                                  responseType: 'blob',
                                })
                              ).data as Blob,
                          })
                        }
                      >
                        미리보기
                      </Button>,
                    ]
                  : []),
                <Button
                  key="download"
                  size="small"
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => void download(item)}
                >
                  받기
                </Button>,
                ...(disabled
                  ? []
                  : [
                      <Popconfirm
                        key="delete"
                        title="이 첨부를 지울까요?"
                        okText="삭제"
                        cancelText="취소"
                        onConfirm={() => remove.mutate(item.attachmentId)}
                      >
                        <Button size="small" type="link" danger icon={<DeleteOutlined />}>
                          삭제
                        </Button>
                      </Popconfirm>,
                    ]),
              ]}
            >
              <List.Item.Meta
                title={
                  <Space size={6}>
                    <Text>{item.fileName}</Text>
                    {item.category && <Tag>{item.category}</Tag>}
                  </Space>
                }
                description={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatBytes(item.fileSize)} · {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}
                    {item.note ? ` · ${item.note}` : ''}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      </Space>

      <FilePreview target={preview} onClose={() => setPreview(null)} />
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}
